import { ALERT_THRESHOLD_TYPES } from '@/modules/Service/constants'
import { createAlerts } from '@/modules/Service/services'
import { ServiceAlert } from '@/modules/Service/types'
import { formatNumberUsage } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FormItem, GridHookForm, Typogaphy, WarningIcons } from 'vnetwork-ui'
import { alertSchema } from './schema'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface AlertThreshold {
    alert_id?: string
    service_configoption_id: number
    name: string
    unit: string
    threshold_type: 'absolute' | 'percentage'
    warning_threshold: number
    critical_threshold: number
    enabled: boolean
    commitment_value: number
}

interface ThresholdProps {
    alerts?: ServiceAlert[]
}

interface AlertFormValues {
    thresholds: AlertThreshold[]
}

const Threshold = ({ alerts = [] }: ThresholdProps) => {
    const { id: serviceID } = useParams()

    const { refetch } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })
    const { t } = useTranslation()

    const formControl = useForm<AlertFormValues>({
        defaultValues: useMemo(
            () => ({
                thresholds: alerts?.map((alert: ServiceAlert) => ({
                    alert_id: alert.id,
                    service_configoption_id: Number(
                        alert.service_configoption_id
                    ),
                    name: alert.name,
                    unit: alert.unit,
                    threshold_type: alert?.threshold_type as
                        | 'absolute'
                        | 'percentage',
                    warning_threshold: alert.warning_threshold,
                    commitment_value: alert.commitment_value,
                    critical_threshold: alert.critical_threshold,
                    enabled: alert.enabled,
                })),
            }),
            [alerts]
        ),
        // @ts-ignore
        resolver: yupResolver(alertSchema),
    })
    const { fields } = useFieldArray({
        control: formControl.control,
        name: 'thresholds',
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: (data: AlertThreshold[]) =>
                createAlerts(Number(serviceID), data),
        },
        {
            successMessage: t('alerts.thresholds.saved-successfully'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                refetch()
            },
        }
    )

    const onSubmit = async (data: AlertFormValues) => {
        await mutateAsync(data.thresholds)
    }

    return (
        <div className="space-y-2">
            {fields && fields.length > 0 ? (
                <GridHookForm
                    formControl={formControl}
                    onSubmit={onSubmit}
                    actions={[
                        {
                            title: t('save-alert-configuration'),
                            variant: 'contained',
                            loading: isPending,
                            disabled:
                                isPending || !formControl.formState.isDirty,
                            type: 'submit',
                        },
                    ]}
                    data={fields.map((threshold, index) => ({
                        className: 'col-span-12',
                        renderEditComponent: () => (
                            <div
                                key={threshold.service_configoption_id}
                                className="p-6 border-l-4 border-orange-400 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border "
                            >
                                <div className="space-y-4">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                                        <div className="flex items-center gap-3">
                                            <WarningIcons className="w-6 h-6 text-orange-500" />
                                            <div>
                                                <Typogaphy className="font-medium text-primary">
                                                    {t(threshold.name)}
                                                </Typogaphy>
                                                <div className="flex items-center gap-2">
                                                    <Typogaphy className="text-gray-500 text-sm">
                                                        {t(
                                                            'alerts.thresholds.commitment_value'
                                                        )}
                                                        :{' '}
                                                        {formatNumberUsage(
                                                            threshold?.commitment_value ||
                                                                0
                                                        )}{' '}
                                                        {threshold?.unit ||
                                                            '---'}
                                                    </Typogaphy>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <FormItem
                                                control={formControl.control}
                                                name={`thresholds.${index}.enabled`}
                                                type="checkbox"
                                                placeholder={
                                                    formControl.watch(
                                                        `thresholds.${index}.enabled`
                                                    )
                                                        ? t('enabled')
                                                        : t(
                                                              'alerts.thresholds.disabled'
                                                          )
                                                }
                                            />
                                        </div>
                                    </div>

                                    {formControl.watch(
                                        `thresholds.${index}.enabled`
                                    ) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <GridHookForm
                                                formControl={formControl}
                                                subForm={true}
                                                actions={[]}
                                                className="!gap-2"
                                                data={[
                                                    {
                                                        className:
                                                            'col-span-12',
                                                        formItemProps: {
                                                            type: 'select',
                                                            allowClear: false,
                                                            options:
                                                                ALERT_THRESHOLD_TYPES(
                                                                    t
                                                                ),
                                                            className:
                                                                'bg-white',
                                                            name: `thresholds.${index}.threshold_type`,
                                                            placeholder: t(
                                                                'alerts.thresholds.choose_threshold_type'
                                                            ),
                                                        },
                                                    },
                                                    {
                                                        className:
                                                            'col-span-12 mt-4',
                                                        renderEditComponent:
                                                            () => (
                                                                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                                                                    {t(
                                                                        'alerts.thresholds.warning_threshold'
                                                                    )}
                                                                </label>
                                                            ),
                                                    },
                                                    {
                                                        className:
                                                            'col-span-12',
                                                        formItemProps: {
                                                            type: 'number',
                                                            name: `thresholds.${index}.warning_threshold`,
                                                            placeholder: t(
                                                                'alerts.thresholds.enter_warning_value'
                                                            ),
                                                        },
                                                    },
                                                    {
                                                        className:
                                                            'col-span-12 mt-4',
                                                        renderEditComponent:
                                                            () => (
                                                                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                                    {t(
                                                                        'alerts.thresholds.critical_threshold'
                                                                    )}
                                                                </label>
                                                            ),
                                                    },
                                                    {
                                                        className:
                                                            'col-span-12',
                                                        formItemProps: {
                                                            type: 'number',
                                                            name: `thresholds.${index}.critical_threshold`,
                                                            placeholder: t(
                                                                'alerts.thresholds.enter_critical_value'
                                                            ),
                                                        },
                                                    },
                                                ]}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ),
                    }))}
                />
            ) : (
                <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                    <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z" />
                    </svg>
                    <Typogaphy className="font-medium text-primary mb-2">
                        {t('no-configurable-options-found')}
                    </Typogaphy>
                    <Typogaphy className="text-gray-500">
                        {t(
                            'this-service-does-not-have-statementable-config-options'
                        )}
                    </Typogaphy>
                </div>
            )}
        </div>
    )
}

export default Threshold
